<template>
  <Modal
    :show="show"
    width="medium"
    :action-text="`Sync Job with ${atsName}`"
    cancel-text=""
    :hide-default-actions="syncButtonState === 'error'"
    @action="sync"
    @close="close"
  >
    <template #title>
      <div
        v-if="syncButtonState === 'success'"
        class="d-flex success-icon"
      >
        <AlertIcon
          type="success"
        />

        <div>
          Job Sync Successful
        </div>
      </div>

      <div
        v-else-if="syncButtonState === 'error'"
        class="d-flex error-icon"
      >
        <AlertIcon
          type="warning"
        />

        <div>
          Job Sync Failed
        </div>
      </div>

      <div
        v-else
        class="d-flex"
      >
        <img
          height="24"
          class="ats-icon mr-2"
          :src="atsIcon || 'https://static.tapwage.com/taprecruit/ats_icons/ats_28.png'"
          :alt="atsName"
        >

        <div>
          Sync Job
        </div>
      </div>
    </template>

    <template #content>
      <div v-if="syncButtonState === 'success'">
        Your job post was successfully synced to {{ atsName }}.
      </div>

      <template v-else-if="syncButtonState === 'error'">
        <div>
          Your job post was not synced to {{ atsName }}.
        </div>

        <div class="mt-2 text-break">
          <DpplSafeHtml
            :html="errorMessage"
          />
        </div>
      </template>

      <div v-else>
        Use the sync button to publish any job updates you've made to {{ atsName }}.
        This will automatically update the job post with your latest edits.
      </div>
    </template>

    <template
      v-if="syncButtonState === 'success'"
      #actions
    >
      <div
        v-if="showAtsEditLink"
        class="d-flex w-100 justify-space-between flex-wrap"
      >
        <v-btn
          class="mt-2"
          variant="outlined"
          color="primary"
          size="small"
          rounded="lg"
          @click="onCopyAtsClick"
        >
          Copy {{ atsName }} URL
        </v-btn>

        <v-btn
          class="mt-2"
          variant="outlined"
          color="primary"
          size="small"
          rounded="lg"
          @click="onOpenAtsClick"
        >
          Open in {{ atsName }}
        </v-btn>
      </div>
      <div v-else />
    </template>
  </Modal>
</template>
<script>
/* eslint-disable vue/require-prop-types */
import { mapActions } from 'pinia';
import { getTooltips } from '@/api/insights';
import { vueSyncWithAts } from '@/api/vue';
import {
  TYPE_SUCCESSFACTORS, TYPE_TALEO,
} from '@/helpers/constants/integrationTypes';
import { fillPlaceholders } from '@/helpers/fillPlaceholders';
import AlertIcon from '@/components/shared/alerts/AlertIcon.vue';
import DpplSafeHtml from '@ui/components/general/DpplSafeHtml/DpplSafeHtml.vue';
import Modal from '@/components/shared/Modal.vue';
import { useAppLoading } from '@/stores/app/loading';
import { useDocumentAtsLink } from '@/stores/document/atsLink';
import { useTracking } from '@/stores/tracking/tracking';

export default {
  name: 'SyncModal',
  components: { AlertIcon, DpplSafeHtml, Modal },

  props: [
    'show',
    'jobId',
    'integrationTypeKey',
    'atsEditUrl',
    'atsName',
    'atsIcon',
    'location',
    'company',
    'title',
    'closeFunction',
  ],

  emits: ['close', 'tracking-event'],

  data() {
    return {
      // enum: 'default', 'loading', 'error', 'success',
      syncButtonState: 'default',
      errorMessage: '',
      errorExpanded: true,
    };
  },

  computed: {
    showAtsEditLink() {
      return ![TYPE_SUCCESSFACTORS, TYPE_TALEO].includes(this.integrationTypeKey);
    },
  },

  methods: {
    ...mapActions(useDocumentAtsLink, ['copyAtsLink', 'openAtsLink']),
    ...mapActions(useAppLoading, ['setLoading']),
    ...mapActions(useTracking, ['syncError']),

    async sync() {
      const { jobId, atsName, integrationTypeKey } = this;
      try {
        this.syncButtonState = 'loading';
        this.setLoading({ modalLoading: true });
        this.errorMessage = '';

        await vueSyncWithAts({ jobId, integrationTypeKey });

        this.syncButtonState = 'success';
        this.setLoading({ modalLoading: false });
        this.$emit('tracking-event', {
          action: 'syncToAts',
          args: { job: this, props: { atsName, integrationTypeKey } },
        });
      } catch (e) {
        this.syncButtonState = 'error';
        this.setLoading({ modalLoading: false });
        const { code } = e.response || {};
        if (code) {
          await this.handleSyncError(e, e.response);
        } else {
          this.syncError({
            job: this,
            props: {
              code: code || e.message,
              status: e.response?.status,
              atsName: this.atsName,
              missing: true,
            },
          });

          this.errorMessage = e.message;
          throw e;
        }
      }
    },

    async handleSyncError(err, { code, vars, status }) {
      this.errorMessage = code;
      const tooltips = (await getTooltips([code])) || {};

      const tooltip = tooltips[code];
      if (tooltip) {
        try {
          this.errorMessage = fillPlaceholders(tooltip.body_r, vars);
        } catch (e) {
          // if templating failed, show raw tooltip content
          this.errorMessage = tooltip.body_r;
          throw e;
        }
      }

      this.syncError({
        job: this,
        props: {
          code,
          status,
          atsName: this.atsName,
          missing: !tooltip,
        },
      });

      if (status >= 500) {
        throw err;
      }
    },

    close() {
      this.syncButtonState = 'default';
      this.$emit('close');
    },

    onCopyAtsClick() {
      this.copyAtsLink();
      this.close();
    },

    onOpenAtsClick() {
      this.openAtsLink();
      this.close();
    },
  },
};
</script>

<style scoped lang="less">
.success-icon {
  :deep(.alert-icon-wrapper) {
    color: @basic-green;
    width: 24px;
    height: 24px;
    border: 1px solid @basic-green;
  }
}
.error-icon {
  :deep(.alert-icon-wrapper) {
    color: @basic-red;
    width: 24px;
    height: 24px;
    border: 1px solid @basic-red;
  }
}
</style>
